import React, { useEffect, useRef } from 'react';
import { TaskFormTitle } from './TaskFormTitle';
import { TaskFormDescription } from './TaskFormDescription';
import { TaskFormDeadline } from './TaskFormDeadline';
import { TagEditor } from './TagEditor';
import { TaskAssigneePicker } from './TaskAssigneePicker';
import { logger } from '../../../utils/logger';

interface TaskFormNewProps {
  title: string;
  description: string;
  tags: string[];
  deadline: Date | null;
  assignedTo: string | null;
  status: 'todo' | 'doing' | 'done';
  projectId?: string;
  availableTags?: string[];
  hasCollaborators?: boolean;
  inputRef?: React.RefObject<HTMLTextAreaElement>;
  onTitleChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onTagsChange: (tags: string[]) => void;
  onDeadlineChange: (date: Date | null) => void;
  onAssigneeChange: (userId: string | null) => void;
  onSubmitNewTask: () => void;
  onCancel: () => void;
}

export const TaskFormNew: React.FC<TaskFormNewProps> = ({
  title,
  description,
  tags,
  deadline,
  assignedTo,
  status,
  projectId,
  availableTags = [],
  hasCollaborators = false,
  inputRef,
  onTitleChange,
  onDescriptionChange,
  onTagsChange,
  onDeadlineChange,
  onAssigneeChange,
  onSubmitNewTask,
  onCancel,
}) => {
  const formRef = useRef<HTMLDivElement>(null);
  const tagInputRef = useRef<HTMLInputElement>(null);

  // Handle key events for form inputs
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && e.currentTarget.classList.contains('title-input')) {
      e.preventDefault();
      if (title.trim() && onSubmitNewTask) {
        // Check for pending tag in the input field
        if (tagInputRef.current && tagInputRef.current.value.trim() && onTagsChange) {
          const pendingTag = tagInputRef.current.value.trim();
          if (!tags.includes(pendingTag)) {
            onTagsChange([...tags, pendingTag]);
          }
          // Clear the input
          tagInputRef.current.value = '';
        }

        onSubmitNewTask();
      } else {
        logger.component('TaskForm: Empty title on Enter, not submitting');
      }
    } else if (
      e.key === 'Enter' &&
      (e.metaKey || e.ctrlKey) &&
      e.currentTarget.tagName === 'TEXTAREA'
    ) {
      e.preventDefault();

      // Check for pending tag in the input field
      if (tagInputRef.current && tagInputRef.current.value.trim() && onTagsChange) {
        const pendingTag = tagInputRef.current.value.trim();
        if (!tags.includes(pendingTag)) {
          onTagsChange([...tags, pendingTag]);
        }
        // Clear the input
        tagInputRef.current.value = '';
      }

      if (onSubmitNewTask && title.trim()) {
        onSubmitNewTask();
      } else {
        logger.component('TaskForm: Empty title on Ctrl+Enter, not submitting');
      }
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  // Handle form submission
  const handleNewTaskSubmit = () => {
    // Debug logging to trace title value
    logger.component('TaskFormNew: About to submit task - debug info', {
      title,
      titleLength: title?.length || 0,
      titleTrimmed: title?.trim(),
      trimmedLength: title?.trim()?.length || 0,
      hasTitle: !!title.trim(),
      type: typeof title,
    });

    // Check for pending tag in the input field
    if (tagInputRef.current && tagInputRef.current.value.trim() && onTagsChange) {
      const pendingTag = tagInputRef.current.value.trim();
      if (!tags.includes(pendingTag)) {
        onTagsChange([...tags, pendingTag]);
      }
      // Clear the input
      tagInputRef.current.value = '';
    }

    // Only submit if title has content
    if (title.trim()) {
      // Set the title in the store one more time right before submitting
      // to ensure the latest value is available
      onTitleChange(title);

      logger.component('TaskFormNew: Submitting task with title', {
        title: title.trim(),
        storeTitle: title, // This will be the same as what we just set
      });

      // Submit the task
      onSubmitNewTask();
    } else {
      logger.component('TaskFormNew: Empty title, not submitting new task');
    }
  };

  // Handle outside clicks to auto-cancel if empty
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Get the click target
      const target = event.target as Element;

      // Check if the click is on a menu button or inside a menu
      const isMenuButton =
        target.closest('[data-menu-button]') ||
        target.closest('[data-task-menu]') ||
        target.matches('.more-vertical') ||
        target.closest('.more-vertical');

      // If the title is empty and we click outside the form, and it's not a menu button, cancel it
      if (
        !title.trim() &&
        formRef.current &&
        !formRef.current.contains(event.target as Node) &&
        !isMenuButton
      ) {
        onCancel();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [title, onCancel]);

  // Reset form fields when title is cleared
  // This effect was causing an infinite loop - let's add a flag to prevent it
  const isResetingRef = useRef(false);

  useEffect(() => {
    // Only reset if we're not already resetting and title is empty
    if (!title && onDeadlineChange && onTagsChange && !isResetingRef.current) {
      // Set the flag to true to prevent infinite calls
      isResetingRef.current = true;

      // Ensure we always start with a clean form
      setTimeout(() => {
        if (description) {
          onDescriptionChange('');
        }
        if (deadline) {
          onDeadlineChange(null);
        }
        if (tags.length > 0) {
          onTagsChange([]);
        }
        if (assignedTo) {
          onAssigneeChange(null);
        }

        // Reset the flag after a timeout
        setTimeout(() => {
          isResetingRef.current = false;
        }, 0);
      }, 0);
    }
  }, [
    title,
    description,
    deadline,
    tags,
    assignedTo,
    onDescriptionChange,
    onDeadlineChange,
    onTagsChange,
    onAssigneeChange,
  ]);

  return (
    <div ref={formRef} className="rounded-[5px] transition-all relative">
      <TaskFormTitle
        value={title}
        onChange={onTitleChange}
        onKeyDown={handleKeyDown}
        isEditMode={false}
        autoFocus={true}
        inputRef={inputRef}
      />

      {/* Conditional content that shows when title is entered */}
      {title.trim() && (
        <>
          <div className="relative mt-2">
            <TaskFormDescription
              value={description}
              onChange={onDescriptionChange}
              onKeyDown={handleKeyDown}
              isEditMode={false}
            />
          </div>

          {/* Deadline picker */}
          <div className="relative mt-2">
            <TaskFormDeadline
              selectedDate={deadline}
              onDateChange={onDeadlineChange}
              isEditMode={false}
            />
          </div>

          {/* Tags section */}
          {onTagsChange && (
            <div className="mt-3">
              <TagEditor
                tags={tags}
                onTagsChange={onTagsChange}
                status={status}
                availableTags={availableTags}
                inputRef={tagInputRef}
                isNewForm={true}
              />
            </div>
          )}

          {/* Assignee Picker with less margin on mobile */}
          {hasCollaborators && (
            <div className="relative mt-3">
              <TaskAssigneePicker
                assignedUserId={assignedTo}
                projectId={projectId}
                onAssigneeChange={onAssigneeChange}
              />
            </div>
          )}

          {/* Control buttons */}
          <div className="flex justify-end mt-8 mb-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={e => {
                  // Stop propagation to prevent parent elements from capturing this event
                  e.stopPropagation();

                  logger.component('TaskForm: Cancel button clicked (new task mode)', {
                    isEditMode: false,
                  });

                  // Call the onCancel callback
                  onCancel();
                }}
                className="flex items-center px-2 py-1 text-sm text-gray-600 hover:text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={e => {
                  // Stop propagation to prevent parent elements from capturing this event
                  e.stopPropagation();
                  if (title.trim()) {
                    handleNewTaskSubmit();
                  } else {
                    logger.component(
                      'TaskForm: Empty title on Add Task button click, not submitting'
                    );
                  }
                }}
                disabled={!title.trim()}
                className={`flex items-center px-3 py-1.5 text-sm rounded-md shadow-sm transition-colors ${
                  title.trim()
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="flex-shrink-0 mr-1.5"
                >
                  <path d="M5 12h14"></path>
                  <path d="M12 5v14"></path>
                </svg>
                <span>Add Task</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
